import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { InputEmail } from '../InputEmail';
import { ROUTE_PATH } from '@/constants/routes';
import { useApiErrorTranslation } from '@/hooks/useApiErrorTranslation';
import { useApiRequest } from '@/hooks/useApiRequest';
import { useAuth } from '@/hooks/useAuth';

interface LoginFormData {
  email: string;
  password: string;
}

export const SharedDeviceLoginForm = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { login, isAuthenticated } = useAuth();
  const { translateApiError } = useApiErrorTranslation();
  const [loginError, setLoginError] = useState<string | null>(null);
  const { isRequesting, execute } = useApiRequest<'POST', 'SharedDeviceLogin'>(
    'POST',
    'SharedDeviceLogin',
  );

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<LoginFormData>({
    defaultValues: {
      email: '',
      password: '',
    },
  });

  // 既にログイン済みの場合は共有端末TOP画面にリダイレクト
  useEffect(() => {
    if (isAuthenticated) {
      navigate(ROUTE_PATH.SHARE_DEVICE);
    }
  }, [isAuthenticated, navigate]);

  const onFormSubmit = async (data: LoginFormData) => {
    setLoginError(null);

    // 共有端末の場合はmembership_typeを送信しない
    const result = await execute(data);

    if (result.ok) {
      // 認証成功時の処理
      const { jwt, expires_at, role } = result.data.data;

      // AuthContextにログイン情報を保存（user情報は後で取得）
      await login(jwt, expires_at, role);

      // 共有端末TOP画面にリダイレクト
      navigate(ROUTE_PATH.SHARE_DEVICE);
    } else {
      const { message } = translateApiError(result.error);
      setLoginError(message);
    }
  };

  return (
    <div className="page page_f0">
      <main className="page__main">
        <div className="page__content">
          <section className="f0">
            <div className="f0__line">
              <div className="f0__inner">
                <div className="f0__area">
                  {/* タイトル画像 */}
                  <div className="f0__title">
                    <img
                      className="f0__title-img"
                      src="/F_1_LOUNGERESERVATION.png"
                      alt="LOUNGE RESERVATION"
                    />
                  </div>

                  {/* ログインフォーム */}
                  <div className="f0__login">
                    <form
                      className="f0__form"
                      onSubmit={handleSubmit(onFormSubmit)}
                      noValidate
                    >
                      <div className="f0__fields">
                        {/* メールアドレス入力 */}
                        <div className="f0__field">
                          <InputEmail
                            className="f0__input"
                            placeholder={t('login.emailPlaceholder')}
                            {...register('email', {
                              required: t('login.validation.emailRequired'),
                              pattern: {
                                value:
                                  /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                                message: t('login.validation.emailInvalid'),
                              },
                            })}
                          />
                          {errors.email && (
                            <p className="f0__alert f0__alert--error">
                              {errors.email.message}
                            </p>
                          )}
                        </div>

                        {/* パスワード入力 */}
                        <div className="f0__field">
                          <input
                            className="f0__input"
                            type="password"
                            placeholder={t('login.passwordPlaceholder')}
                            {...register('password', {
                              required: t('login.validation.passwordRequired'),
                            })}
                          />
                          {errors.password && (
                            <p className="f0__alert f0__alert--error">
                              {errors.password.message}
                            </p>
                          )}
                        </div>
                      </div>

                      {/* ログインボタン */}
                      <div className="f0__actions">
                        <button
                          className="f0__btn f0__btn--login"
                          type="submit"
                          disabled={isRequesting}
                        >
                          {isRequesting
                            ? t('login.loginButtonLoading')
                            : t('login.loginButton')}
                        </button>
                      </div>
                    </form>

                    {/* エラーメッセージ */}
                    {loginError && (
                      <div className="f0__alerts">
                        <div
                          className={`f0__alert ${
                            loginError === t('login.errors.networkError')
                              ? 'f0__alert--offline'
                              : 'f0__alert--error'
                          }`}
                        >
                          {loginError}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </section>
        </div>
      </main>
    </div>
  );
};
